import {
  ForwardedRef,
  RefObject,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames/bind';
import styles from './TableRow.module.css';

import { Group, GroupMember, Student } from '../../../types/classroom';

import useModal from '../../../hooks/useModal';

import Button from '../../../components/Button';
import MultipleSelectBox from '../../../components/MultipleSelectBox';
import Details from '../Details';
import { isOfType } from '../../../utils/typeGuard';
import { useLocation } from 'react-router';
import { COLLAPSE, DELETE, OPEN } from '../../../constants';
import { TooltipWrapper } from '../../../components/Tooltip';

interface TableRow {
  name: string;
  list: string[];
  item: Student | Group | GroupMember;
  parentSeq?: number;
  selectedColumn?: string;
}

const TableRow = forwardRef(
  (
    { name, list, item, parentSeq, selectedColumn }: TableRow,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const cx = classNames.bind(styles);
    const { toggleModal } = useModal();
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const { state } = useLocation();

    const toggleDetails = () => {
      setIsDetailsOpen(!isDetailsOpen);
    };

    const openRemoveModal = () => {
      toggleModal({
        id: 'confirmRemoveTableRow',
        type: 'confirm',
        isOpen: true,
        data: {
          type: name,
          seq: item.seq,
          parentSeq: parentSeq,
          status: item.status,
        },
      });
    };

    useEffect(() => {
      if (name === 'student' && item.studentUserId === state?.userId) {
        setIsDetailsOpen(true);
      }
    }, []);

    return (
      <div
        key={item.seq}
        className={cx('items', {
          active: isDetailsOpen,
        })}
        ref={ref as RefObject<HTMLDivElement>}
      >
        {list.map(
          (header) =>
            !['dispStatus', 'details'].includes(header) && (
              <div
                key={header}
                className={cx(
                  'sub',
                  { tal: header === 'email' },
                  { show: selectedColumn === header },
                  { hide: header === 'email' && selectedColumn !== 'email' }
                )}
              >
                {isOfType(item, 'groupList') && header === 'groupList' ? (
                  <MultipleSelectBox item={item} list={item.groupList} />
                ) : (
                  <span>{item[header] || '-'}</span>
                )}
              </div>
            )
        )}

        <div className={cx('sub', 'dispStatus')}>
          {item.status !== 1 && (
            <span className={cx('status_request')}>{item.dispStatus}</span>
          )}
          <TooltipWrapper content={DELETE}>
            {(tooltipProps) => (
              <Button
                name={DELETE}
                isBlindName={true}
                handleClick={openRemoveModal}
                tooltipProps={tooltipProps}
              >
                <i className={cx('svg_i')}></i>
              </Button>
            )}
          </TooltipWrapper>
        </div>

        {name !== 'groupMember' && (
          <div className={cx('sub', 'details')}>
            <TooltipWrapper content={isDetailsOpen ? COLLAPSE : OPEN}>
              {(tooltipProps) => (
                <Button
                  name={isDetailsOpen ? COLLAPSE : OPEN}
                  isBlindName={true}
                  classnames={['table_details']}
                  handleClick={toggleDetails}
                  tooltipProps={tooltipProps}
                >
                  <i className={cx('svg_i')}></i>
                </Button>
              )}
            </TooltipWrapper>
          </div>
        )}

        {isDetailsOpen && (
          <Details name={name} info={item} isOpen={isDetailsOpen} />
        )}
      </div>
    );
  }
);

export default TableRow;
